import { Button, Card, CardBody, Col, Modal } from "react-bootstrap";
import languageES from "datatables.net-plugins/i18n/es-MX";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import api from "@/api/api.js";
import $ from "jquery";
import "datatables.net";
import { capitalizeFirstLetter } from "@/utils/stringUtils.js";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const BillsAdmin = () => {
  const { t } = useTranslation();
  const tableRef = useRef(null);
  const dataTable = useRef(null);
  const navigate = useNavigate();

  const [showWorkModal, setShowWorkModal] = useState(false);
  const [workTarget, setWorkTarget] = useState(null);
  const [working, setWorking] = useState(false);

  const BILLING_STATUS_VARIANT = {
    1: "secondary",
    2: "success",
    3: "danger",
    4: "warning",
  };

  const renderStatusBadge = (status, label) => {
    const variant = BILLING_STATUS_VARIANT[status] ?? "dark";
    return `<span class="badge bg-${variant}">${label}</span>`;
  };

  const handleConfirmWork = async () => {
    if (!workTarget || working) return;
    setWorking(true);
    try {
      await api.post(`/billings/${workTarget.id}/work`);
      setShowWorkModal(false);
      navigate(`/billsAdmin/${workTarget.id}/show`);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "No se pudo trabajar la factura",
        text:
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Intenta de nuevo.",
      });
      if (dataTable.current) dataTable.current.ajax.reload();
    } finally {
      setWorking(false);
    }
  };

  useEffect(() => {
    if (!tableRef.current) return;
    const tableElement = tableRef.current;

    const STYLE_ID = "bills-row-style";
    if (!document.getElementById(STYLE_ID)) {
      const styleEl = document.createElement("style");
      styleEl.id = STYLE_ID;
      styleEl.textContent = `
        .bill-status-1 td { background-color: #fff8e1 !important; }
        .bill-status-2 td { background-color: #e8f5e9 !important; }
        .bill-status-3 td { background-color: #fdecea !important; }
        .bill-status-4 td { background-color: #fff3cd !important; }
        .bill-status-5 td { background-color: #eeeeee !important; }
      `;
      document.head.appendChild(styleEl);
    }

    if ($.fn.DataTable.isDataTable(tableElement)) {
      $(tableElement).DataTable().clear().destroy();
      $(tableElement).empty();
    }

    const dtInstance = $(tableElement).DataTable({
      language: languageES,
      processing: true,
      serverSide: true,
      createdRow: (row, data) => {
        if (data?.billingStatus)
          $(row).addClass(`bill-status-${data.billingStatus}`);
      },
      ajax: async (dtParams, callback) => {
        try {
          const order = dtParams.order[0];
          const columnName = dtParams.columns[order.column].name;
          const response = await api.get(`/billings?requestedBy=not_null`, {
            params: {
              page: dtParams.start / dtParams.length + 1,
              itemsPerPage: dtParams.length,
              [`order[${columnName}]`]: order.dir,
              search: dtParams.search?.value || undefined,
            },
          });
          callback({
            draw: dtParams.draw,
            recordsTotal: response.data["hydra:totalItems"],
            recordsFiltered: response.data["hydra:totalItems"],
            data: response.data["hydra:member"],
          });
        } catch {
          callback({
            draw: dtParams.draw,
            recordsTotal: 0,
            recordsFiltered: 0,
            data: [],
          });
        }
      },
      columns: [
        {
          // La prioritaria ya viene primero del servidor; esto es para que se
          // vea por qué, sin tener que saberse los paquetes de memoria.
          title: capitalizeFirstLetter(t("folio")),
          data: "id",
          name: "id",
          render: (data, type, row) =>
            row?.priority
              ? `<span class="badge bg-warning text-dark me-2" title="${t("ui.priorityAttention")}"><i class="bi bi-lightning-charge-fill"></i></span>${data}`
              : data,
        },
        {
          title: capitalizeFirstLetter(t("company")),
          render: (data, type, row) => row?.company?.name ?? "",
          orderable: false,
        },
        {
          title: capitalizeFirstLetter(t("requested by")),
          render: (data, type, row) =>
            row?.requestedBy?.userProfile?.fullName ?? "",
          orderable: false,
        },
        {
          title: capitalizeFirstLetter(t("requested to")),
          render: (data, type, row) => row?.contact?.businessName ?? "",
          orderable: false,
        },
        {
          title: capitalizeFirstLetter(t("requested")),
          data: "createdAtAgo",
          name: "updatedAt",
        },
        {
          title: capitalizeFirstLetter(t("status")),
          data: "billingStatus",
          name: "billingStatus",
          render: (data, type, row) =>
            renderStatusBadge(row.billingStatus, row.billingStatusLabel),
        },
        {
          title: "Trabajada por",
          orderable: false,
          render: (data, type, row) => {
            const who = row?.workedBy?.userProfile?.fullName;
            return who
              ? `<span class="text-success">Trabajando por: ${who}</span>`
              : `<span class="text-muted">Activa para trabajar</span>`;
          },
        },
        {
          title: capitalizeFirstLetter(t("acciones")),
          orderable: false,
          render: (data, type, row) => {
            const work = row?.workedBy
              ? ""
              : `<button class="btn btn-primary btn-sm btn-work-billing" data-id="${row?.id ?? ""}" data-rfc="${row?.contact?.taxIdentification ?? row?.taxIdentification ?? ""}" data-company="${row?.company?.name ?? ""}">Trabajar Factura</button>`;
            return `<div class="d-flex gap-1">
                      ${work}
                      <button class="btn btn-warning btn-sm btn-detail-billing-admin" data-id="${row?.id ?? ""}">Detalle</button>
                    </div>`;
          },
        },
      ],
    });

    dataTable.current = dtInstance;

    const handleShowClick = function (e) {
      e.preventDefault();
      const billingId = $(this).data("id");
      if (billingId) navigate(`/billsAdmin/${billingId}/show`);
    };

    const handleWorkClick = function (e) {
      e.preventDefault();
      setWorkTarget({
        id: $(this).data("id"),
        rfc: $(this).data("rfc"),
        company: $(this).data("company"),
      });
      setShowWorkModal(true);
    };

    $(tableElement).on("click", ".btn-detail-billing-admin", handleShowClick);
    $(tableElement).on("click", ".btn-work-billing", handleWorkClick);

    return () => {
      $(tableElement).off("click", ".btn-detail-billing-admin", handleShowClick);
      $(tableElement).off("click", ".btn-work-billing", handleWorkClick);
      if ($.fn.DataTable.isDataTable(tableElement)) {
        $(tableElement).DataTable().clear().destroy();
      }
    };
  }, [navigate, t]);

  return (
    <>
      <Col xs={12}>
        <Card>
          <Card.Header>Lista de facturas solicitadas</Card.Header>
          <CardBody>
            <table
              ref={tableRef}
              className="display"
              style={{ width: "100%" }}
            />
          </CardBody>
        </Card>
      </Col>

      <Modal show={showWorkModal} onHide={() => setShowWorkModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Trabajar factura</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Deseas tomar esta factura para trabajarla?</p>
          <ul className="list-unstyled mb-0">
            <li>
              <strong>RFC (receptor):</strong> {workTarget?.rfc || "—"}
            </li>
            <li>
              <strong>Empresa:</strong> {workTarget?.company || "—"}
            </li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowWorkModal(false)}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleConfirmWork} disabled={working}>
            {working ? "Procesando..." : "Confirmar y trabajar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BillsAdmin;
